/**
 * Ad Unit Catalog API
 *
 * Backs the "Create new ad unit" picker UI. The catalog endpoint is the
 * advertiser-scoped allowlist of valid content types + sizes (filtered by
 * `allowed_templates` and `AD_UNIT_SIZES` server-side). It does NOT replace
 * create/update endpoints — those still live under
 * `/campaigns/:campaign_id/ad_units` and accept a `content_type` + `sizes`
 * array. The catalog only tells the FE what the user is *allowed* to pick.
 *
 *   GET /advertisers/:advertiser_id/ad_unit_catalog
 *
 * The endpoint is the same for both user and admin sessions — auth is
 * handled by the bearer token, no role-based path rewriting needed.
 *
 * The server response shape isn't fully locked down yet — historically the
 * FE had two parallel constants (`CONTENT_TYPE_ROWS` grouped, and a flat
 * `PRESET_SIZES`), and the BE could ship either shape. We normalise both
 * into one internal form so the modal doesn't care.
 */

import { apiClient } from './client'

// ---------- Normalised types the UI consumes ----------

export interface CatalogContentType {
  /** Backend value, e.g. `"single_ad"`, sent as `content_type` on create. */
  value: string
  /** Human label, e.g. `"Single Ad"`. */
  label: string
  description?: string
  /** Group / section header, e.g. `"Display"`. Falls back to `"Other"`. */
  group?: string
  /**
   * Allowlist of size strings (e.g. `"300x250"`) compatible with this
   * content type. `undefined`/`null`/empty means: any size in the catalog
   * applies. We treat absence as "no restriction" so older shapes that
   * don't carry per-type sizes still render correctly.
   */
  allowed_sizes?: string[] | null
}

export interface CatalogSize {
  /** `"WxH"` canonical form. */
  size: string
  /** Optional friendly name, e.g. `"Inline Rectangle"`. */
  label?: string
  w: number
  h: number
}

export interface AdUnitCatalog {
  content_types: CatalogContentType[]
  sizes: CatalogSize[]
}

// ---------- Possible raw shapes from the server ----------

type RawType = {
  value?: string
  key?: string
  content_type?: string
  label?: string
  name?: string
  title?: string
  description?: string
  group?: string
  category?: string
  section?: string
  allowed_sizes?: string[] | null
  sizes?: string[] | null
}

type RawRow = {
  title?: string
  group?: string
  name?: string
  types?: RawType[]
  content_types?: RawType[]
  items?: RawType[]
}

type RawSize =
  | string
  | {
      size?: string
      value?: string
      label?: string
      name?: string
      w?: number
      h?: number
      width?: number
      height?: number
    }

interface RawCatalog {
  rows?: RawRow[]
  groups?: RawRow[]
  content_types?: RawType[] | RawRow[]
  types?: RawType[]
  sizes?: RawSize[]
  allowed_sizes?: RawSize[]
  // some payloads wrap the whole thing under a key
  ad_unit_catalog?: RawCatalog
  catalog?: RawCatalog
}

// ---------- Helpers ----------

function parseSize(raw: RawSize): CatalogSize | null {
  if (typeof raw === 'string') {
    const m = raw.match(/^\s*(\d+)\s*[x×]\s*(\d+)\s*$/i)
    if (!m) return null
    return { size: `${m[1]}x${m[2]}`, w: parseInt(m[1], 10), h: parseInt(m[2], 10) }
  }
  if (!raw || typeof raw !== 'object') return null
  const sizeStr = raw.size || raw.value
  if (sizeStr) {
    const m = sizeStr.match(/^\s*(\d+)\s*[x×]\s*(\d+)\s*$/i)
    if (m) {
      return {
        size: `${m[1]}x${m[2]}`,
        label: raw.label || raw.name,
        w: typeof raw.w === 'number' ? raw.w : typeof raw.width === 'number' ? raw.width : parseInt(m[1], 10),
        h: typeof raw.h === 'number' ? raw.h : typeof raw.height === 'number' ? raw.height : parseInt(m[2], 10),
      }
    }
  }
  if (typeof raw.w === 'number' && typeof raw.h === 'number') {
    return { size: `${raw.w}x${raw.h}`, label: raw.label || raw.name, w: raw.w, h: raw.h }
  }
  if (typeof raw.width === 'number' && typeof raw.height === 'number') {
    return {
      size: `${raw.width}x${raw.height}`,
      label: raw.label || raw.name,
      w: raw.width,
      h: raw.height,
    }
  }
  return null
}

function parseType(raw: RawType, fallbackGroup?: string): CatalogContentType | null {
  const value = raw.value || raw.key || raw.content_type
  if (!value) return null
  const label = raw.label || raw.name || raw.title || value
  const allowed = raw.allowed_sizes ?? raw.sizes ?? null
  return {
    value,
    label,
    description: raw.description,
    group: raw.group || raw.category || raw.section || fallbackGroup,
    allowed_sizes: Array.isArray(allowed) && allowed.length > 0 ? allowed : null,
  }
}

function unwrap(raw: any): RawCatalog {
  if (!raw || typeof raw !== 'object') return {}
  if (raw.ad_unit_catalog && typeof raw.ad_unit_catalog === 'object') return raw.ad_unit_catalog
  if (raw.catalog && typeof raw.catalog === 'object') return raw.catalog
  return raw
}

export function normaliseCatalog(raw: any): AdUnitCatalog {
  const root = unwrap(raw)
  const types: CatalogContentType[] = []

  // Shape A: grouped rows under `rows` / `groups` / `content_types` (when it
  // contains row-like entries with a nested `types`/`items` array).
  const rowSource: RawRow[] | undefined =
    root.rows ||
    root.groups ||
    (Array.isArray(root.content_types) &&
      root.content_types.length > 0 &&
      // detect row-like entries by presence of nested type list
      (root.content_types as any[]).some(
        r => r && typeof r === 'object' && Array.isArray((r as RawRow).types || (r as RawRow).items),
      )
      ? (root.content_types as RawRow[])
      : undefined)

  if (rowSource) {
    rowSource.forEach(row => {
      const group = row.title || row.group || row.name
      const list = row.types || row.content_types || row.items || []
      list.forEach(t => {
        const parsed = parseType(t, group)
        if (parsed) types.push(parsed)
      })
    })
  } else {
    // Shape B: flat list under `content_types` / `types`.
    const flat: RawType[] = []
    if (Array.isArray(root.content_types)) flat.push(...(root.content_types as RawType[]))
    if (Array.isArray(root.types)) flat.push(...root.types)
    flat.forEach(t => {
      const parsed = parseType(t)
      if (parsed) types.push(parsed)
    })
  }

  // Sizes: top-level `sizes` or `allowed_sizes`.
  const rawSizes = (root.sizes || root.allowed_sizes || []) as RawSize[]
  const sizesByKey = new Map<string, CatalogSize>()
  rawSizes.forEach(rs => {
    const parsed = parseSize(rs)
    if (parsed && !sizesByKey.has(parsed.size)) {
      sizesByKey.set(parsed.size, parsed)
    }
  })

  return {
    content_types: types,
    sizes: Array.from(sizesByKey.values()),
  }
}

// ---------- Path + cache ----------

function getCatalogPath(advertiserId: number): string {
  return `/advertisers/${advertiserId}/ad_unit_catalog`
}

const catalogCache = new Map<number, Promise<AdUnitCatalog>>()

export function invalidateCatalog(advertiserId?: number): void {
  if (advertiserId == null) {
    catalogCache.clear()
    return
  }
  catalogCache.delete(advertiserId)
}

export const adUnitCatalogApi = {
  /**
   * Fetch (and cache) the catalog for one advertiser. The cache is a
   * `Map<advertiserId, Promise<...>>` so concurrent callers share one
   * in-flight request, and successful results stick around for the life
   * of the page. Use {@link invalidateCatalog} to force a re-fetch when
   * the advertiser context changes or the allowlist needs refreshing.
   */
  getCatalog: async (advertiserId: number): Promise<AdUnitCatalog> => {
    const cached = catalogCache.get(advertiserId)
    if (cached) return cached

    const path = getCatalogPath(advertiserId)
    const promise = apiClient
      .get<any>(path, { skipRoleRouting: true })
      .then(raw => normaliseCatalog(raw))
      .catch(err => {
        // Drop failures from the cache so the next attempt actually retries.
        catalogCache.delete(advertiserId)
        throw err
      })
    catalogCache.set(advertiserId, promise)
    return promise
  },
}
